import {World,Vec2,Box,Polygon,WeldJoint,PrismaticJoint} from 'planck';
import {rockTriangles,rectangle,overlaps,intersectsTerrain} from './geometry.js';

export const STEP=1/120, SECTION=.64, WIDTH=.95;
const clamp=(x,a,b)=>Math.max(a,Math.min(b,x));
const distance=(a,b)=>Math.hypot(a.x-b.x,a.y-b.y);

export class Climb {
  constructor(level, equipment={}, options={}) {
    this.level=level; this.equipment={braces:equipment.braces||0,fast:!!equipment.fast};
    this.world=new World(Vec2(0,-9));
    this.world.setAllowSleeping(false);
    this.sections=[];this.connections=[];this.events=[];
    this.t=0;this.status='playing';this.started=false;this.time=0;this.buildClock=0;
    this.maxHeight=0;this.fragment=false;this.braces=this.equipment.braces;
    this.steering=0;this.direction=0;this.buildVertical=1;this.wind=0;this.buildPulse=0;this.breaks=0;this.fallTime=0;this.blocked=null;this.lastImpact=0;this.traverseClock=0;
    this.exitMin=level.exitSide>0?level.exitInset:-5;
    this.exitMax=level.exitSide>0?5:-level.exitInset;
    this.base=this.world.createBody({position:Vec2(0,-.22)});
    this.base.createFixture(Box(1.4,.22),{friction:.8});
    this.solids=[...(level.obstacles||[]).map(r=>({...r,kind:'obstacle'})),{x:(this.exitMin+this.exitMax)/2,y:level.height,width:this.exitMax-this.exitMin,depth:3.6,kind:'landing'}].map(r=>({...r,triangles:rockTriangles(r)}));
    for(const solid of this.solids){
      solid.body=this.world.createBody();solid.body.setUserData({kind:'terrain'});
      for(const triangle of solid.triangles)solid.body.createFixture(Polygon(triangle),{friction:.6});
    }
    this.platform=this.solids.at(-1).body;
    this.player=this.world.createDynamicBody({position:Vec2(0,.55),angularDamping:2,linearDamping:.12,bullet:true});
    this.playerFixture=this.player.createFixture(Box(.15,.26),{density:6,friction:.7,restitution:.03,filterCategoryBits:2,filterMaskBits:1});
    this.player.setUserData({kind:'player'});
    this.previous=new Map();
    this.options=options;
    this.addSection(0,true);
    this.previous.clear();this.remember();
    this.world.on('post-solve',(contact,impulse)=>{
      const power=Math.max(...impulse.normalImpulses);
      const a=contact.getFixtureA().getBody(),b=contact.getFixtureB().getBody();
      const speed=distance(a.getLinearVelocity(),b.getLinearVelocity());
      // Static weight resting on a ledge is not a new impact every physics step.
      if(power>2.2&&speed>.6&&this.t>1&&this.t-this.lastImpact>.08){this.lastImpact=this.t;this.events.push({type:'impact',power:Math.min(power,15),position:a.getPosition().clone()});}
    });
  }
  get top(){return this.support||this.sections.at(-1)?.body;}
  get feet(){return this.player.getWorldPoint(Vec2(0,-.26));}
  get height(){return this.feet.y;}
  clearLine(from,to){let clear=true;this.world.rayCast(from,to,fixture=>{if(fixture.getBody().getUserData()?.kind==='terrain'){clear=false;return 0;}return -1;});return clear;}
  remember(){for(let b=this.world.getBodyList();b;b=b.getNext())this.previous.set(b,{x:b.getPosition().x,y:b.getPosition().y,a:b.getAngle()});}
  pose(body,alpha=1){const p=body.getPosition(),old=this.previous.get(body)||{x:p.x,y:p.y,a:body.getAngle()};return{x:old.x+(p.x-old.x)*alpha,y:old.y+(p.y-old.y)*alpha,a:old.a+(body.getAngle()-old.a)*alpha};}
  connected(body){
    const links=[];
    for(const connection of this.connections){
      if(connection.broken)continue;
      const other=connection.lower===body?connection.body:connection.body===body?connection.lower:null;
      if(other&&other!==this.base)links.push({connection,other,offset:body.getLocalPoint(other.getPosition())});
    }
    return links;
  }
  grounded(body=this.top){
    if(!body)return false;
    const seen=new Set([body]),queue=[body];
    while(queue.length){
      const current=queue.shift();
      for(const connection of this.connections){
        if(connection.broken)continue;
        const other=connection.lower===current?connection.body:connection.body===current?connection.lower:null;
        if(!other)continue;if(other===this.base)return true;
        if(!seen.has(other)){seen.add(other);queue.push(other);}
      }
    }
    return false;
  }
  verticalNeighbor(body,direction){return this.connected(body).find(link=>link.offset.y*Math.sign(direction)>SECTION*.42&&Math.abs(link.offset.x)<WIDTH*.58);}
  mountPoint(body,side=0){return side?body.getWorldPoint(Vec2(side*(WIDTH/2+.2),0)):body.getWorldPoint(Vec2(0,SECTION/2+.28));}
  attachPlayer(body,side=0){
    if(this.playerJoint){this.world.destroyJoint(this.playerJoint);this.playerJoint=null;}
    this.playerFixture.setFilterData({categoryBits:2,maskBits:1,groupIndex:0});
    this.support=body;this.mountSide=side;this.traversal=null;
    const p=this.mountPoint(body,side);
    this.player.setTransform(p,body.getAngle());
    this.player.setLinearVelocity(body.getLinearVelocityFromWorldPoint(p));
    this.player.setAngularVelocity(body.getAngularVelocity());
    this.playerJoint=this.world.createJoint(new PrismaticJoint({enableMotor:true,maxMotorForce:16,motorSpeed:0,enableLimit:true,lowerTranslation:-.3,upperTranslation:.3},body,this.player,p,body.getWorldVector(side?Vec2(0,1):Vec2(1,0))));
    this.previous.set(this.player,{x:p.x,y:p.y,a:body.getAngle()});
  }
  placement(steer=0,allowReuse=true,vertical=1){
    const lower=this.top,direction=clamp(steer,-1,1);
    let a=lower.getAngle(),reuse=null;
    // Full left/right builds a horizontal arm. With no horizontal input, S
    // reverses the vertical direction; Space or Space+W continues upward.
    const verticalDirection=Math.abs(direction)>.5?0:vertical<0?-1:1;
    const local=Vec2(direction*WIDTH*.98,SECTION*verticalDirection);
    // A connected sideways frame is a path back out of a dead end, not a new
    // frame to duplicate. Require a live joint and an adjacent, walkable deck.
    if(allowReuse&&Math.abs(direction)>.5)for(const c of this.connections){
      if(c.broken)continue;
      const other=c.lower===lower?c.body:c.body===lower?c.lower:null;
      if(!other||other===this.base)continue;
      const offset=lower.getLocalPoint(other.getPosition());
      if(Math.abs(offset.x-Math.sign(direction)*WIDTH*.98)<.22&&Math.abs(offset.y)<SECTION*.4&&Math.abs(other.getAngle()-a)<.35){reuse=other;break;}
    }
    const p=reuse?reuse.getPosition().clone():lower.getWorldPoint(local);
    if(reuse)a=reuse.getAngle();
    const old=lower.getPosition(),oldPlayer=this.player.getPosition();
    const mountSide=reuse&&this.verticalNeighbor(reuse,1)?Math.sign(oldPlayer?.x-reuse.getPosition().x)||1:0;
    const player=reuse?this.mountPoint(reuse,mountSide):Vec2(p.x-Math.sin(a)*(SECTION/2+.28),p.y+Math.cos(a)*(SECTION/2+.28));
    let reason=Math.abs(a)>1.25?'lean':null;
    // Sweep both the new frame and builder at <= 5 cm intervals. A clear endpoint
    // alone would permit popping through thin ledges or the platform underside.
    const steps=Math.ceil(Math.max(distance(old,p),distance(oldPlayer,player))/.05);
    for(let i=1;!reason&&i<=steps;i++){
      const t=i/steps,frame={x:old.x+(p.x-old.x)*t,y:old.y+(p.y-old.y)*t},person={x:oldPlayer.x+(player.x-oldPlayer.x)*t,y:oldPlayer.y+(player.y-oldPlayer.y)*t};
      const personAngle=reuse?this.player.getAngle()+(a-this.player.getAngle())*t:a;
      const personShape=rectangle(person,personAngle,.32,.54);
      if((!reuse&&intersectsTerrain(rectangle(frame,a,WIDTH,SECTION),this.solids))||intersectsTerrain(personShape,this.solids))reason='rock';
      if(reuse&&!reason){
        const junction=new Set([lower,reuse]);for(const link of [...this.connected(lower),...this.connected(reuse)])junction.add(link.other);
        if(this.sections.some(s=>!junction.has(s.body)&&overlaps(personShape,rectangle(s.body.getPosition(),s.body.getAngle(),WIDTH,SECTION))))reason='frame';
      }
    }
    if(!reason&&!reuse){const poly=rectangle(p,a,WIDTH,SECTION);for(const s of this.sections)if(s.body!==lower&&overlaps(poly,rectangle(s.body.getPosition(),s.body.getAngle(),WIDTH,SECTION),.04)){reason='frame';break;}}
    return {p,a,local,player,blocked:reason,reuse,mountSide,verticalDirection};
  }
  addSection(steer=0,initial=false,vertical=1){
    if(!initial&&(!this.playerJoint||this.status!=='playing'))return false;
    const lower=this.top||this.base;
    const candidate=initial?{p:Vec2(0,SECTION/2),a:0,verticalDirection:1}:this.placement(steer,false,vertical);
    this.blocked=candidate.blocked||null;
    if(this.blocked)return false;
    if(!initial&&this.sections.length>=100)return false;
    const {p,a}=candidate;
    const body=this.world.createDynamicBody({position:p,angle:a,angularDamping:.22,linearDamping:.025});
    body.createFixture(Box(WIDTH/2,SECTION/2),{density:1.25,friction:.65,restitution:.03,filterCategoryBits:1,filterMaskBits:3});
    body.setUserData({kind:'section',index:this.sections.length});
    if(!initial){body.setLinearVelocity(lower.getLinearVelocityFromWorldPoint(p));body.setAngularVelocity(lower.getAngularVelocity());}
    const point=initial?Vec2(0,0):Vec2((lower.getPosition().x+p.x)/2,(lower.getPosition().y+p.y)/2);
    const flexible=!initial&&this.sections.length%5===0;
    const joint=this.world.createJoint(new WeldJoint({frequencyHz:flexible?this.level.frequency:0,dampingRatio:.6},lower,body,point));
    this.connections.push({joint,lower,body,reference:body.getAngle()-lower.getAngle(),overload:0,stress:0,broken:false,braced:false,flexible,capacity:this.level.strength*(initial?1.8:1)});
    this.sections.push({body,index:this.sections.length,age:0,direction:initial?0:steer,vertical:candidate.verticalDirection});
    this.previous.set(body,{x:p.x,y:p.y,a});
    this.attachPlayer(body);
    this.buildPulse=1;
    if(!initial)this.events.push({type:'build',position:p.clone(),dx:p.x-lower.getPosition().x,dy:p.y-lower.getPosition().y});
    return true;
  }
  walk(horizontal){
    if(!this.playerJoint||this.traversal||this.status!=='playing'||!horizontal)return false;
    const candidate=this.placement(horizontal,true);
    if(candidate.blocked){this.blocked=candidate.blocked;return false;}
    if(!candidate.reuse){this.blocked=candidate.blocked||'route';return false;}
    const fromBody=this.top,from=this.player.getPosition().clone(),target=candidate.reuse,side=candidate.mountSide;
    const p=this.mountPoint(target,side),delta=Vec2(p.x-from.x,p.y-from.y),length=Math.max(.001,distance(from,p)),axis=Vec2(delta.x/length,delta.y/length);
    this.world.destroyJoint(this.playerJoint);
    this.playerFixture.setFilterData({categoryBits:2,maskBits:0,groupIndex:0});
    this.playerJoint=this.world.createJoint(new PrismaticJoint({enableMotor:true,maxMotorForce:95,motorSpeed:length/.3,enableLimit:true,lowerTranslation:0,upperTranslation:length},fromBody,this.player,from,axis));
    this.mountSide=side;this.traversal={from:fromBody,target,side,direction:Math.sign(horizontal),age:0,duration:.3,distance:length,kind:'walk'};this.blocked=null;
    this.events.push({type:'walk',position:from.clone(),dx:delta.x,dy:delta.y,direction:Math.sign(horizontal)});
    return true;
  }
  traverse(vertical){
    if(!this.playerJoint||this.traversal||this.status!=='playing'||!vertical)return false;
    const fromBody=this.top,from=this.player.getPosition().clone(),direction=Math.sign(vertical);
    const candidates=this.connected(fromBody).filter(link=>link.offset.y*direction>SECTION*.42&&Math.abs(link.offset.x)<WIDTH*.58).map(link=>({other:link.other,score:Math.abs(link.offset.y-SECTION*direction)}));
    const target=candidates.sort((a,b)=>a.score-b.score)[0]?.other;
    if(!target){this.blocked='route';return false;}
    const angle=target.getAngle(),preferred=this.mountSide||Math.sign(from.x-target.getPosition().x)||1;
    const junctionBodies=new Set([fromBody,target]);
    for(const link of [...this.connected(fromBody),...this.connected(target)])junctionBodies.add(link.other);
    let route=null,blocked='route';
    // A junction can have a branch against one face. Try the builder's current
    // face first, then let him climb around the open face of the same frame.
    // The sweep still prevents either choice from tunnelling through rock or a
    // third scaffold member.
    for(const side of [preferred,-preferred]){
      const p=this.mountPoint(target,side),steps=Math.ceil(distance(from,p)/.04);
      let reason=null;
      for(let i=1;!reason&&i<=steps;i++){
        const t=i/steps,person={x:from.x+(p.x-from.x)*t,y:from.y+(p.y-from.y)*t};
        const personAngle=this.player.getAngle()+(angle-this.player.getAngle())*t;
        if(intersectsTerrain(rectangle(person,personAngle,.32,.54),this.solids))reason='rock';
        for(const section of this.sections){
          // Members welded into either junction are the rungs and corner the
          // builder is actively negotiating. More distant frames still block.
          if(reason||junctionBodies.has(section.body))continue;
          if(overlaps(rectangle(person,personAngle,.32,.54),rectangle(section.body.getPosition(),section.body.getAngle(),WIDTH,SECTION)))reason='frame';
        }
      }
      if(!reason){route={side,p};break;}
      if(reason==='rock'||blocked==='route')blocked=reason;
    }
    if(!route){this.blocked=blocked;return false;}
    const {side,p}=route;
    const delta=Vec2(p.x-from.x,p.y-from.y),length=Math.max(.001,distance(from,p)),axis=Vec2(delta.x/length,delta.y/length);
    this.world.destroyJoint(this.playerJoint);
    // Clearance is swept before the climb begins. Disable only the builder's
    // section contact during the motorized move so the body can pass the rungs;
    // its mass remains on the real source joint throughout the visible climb.
    this.playerFixture.setFilterData({categoryBits:2,maskBits:0,groupIndex:0});
    this.playerJoint=this.world.createJoint(new PrismaticJoint({enableMotor:true,maxMotorForce:95,motorSpeed:length/.36,enableLimit:true,lowerTranslation:0,upperTranslation:length},fromBody,this.player,from,axis));
    this.mountSide=side;this.traversal={from:fromBody,target,side,direction,age:0,duration:.36,distance:length,kind:'climb'};this.blocked=null;
    this.events.push({type:'climb',position:from.clone(),dx:delta.x,dy:delta.y,direction});
    return true;
  }
  brace(){
    if(this.braces<1||this.status!=='playing')return false;
    const candidate=this.connections.filter(c=>!c.broken&&!c.braced).sort((a,b)=>b.stress-a.stress)[0];
    if(!candidate)return false;
    candidate.braced=true;candidate.capacity*=2.2;candidate.overload*=.3;
    candidate.joint.setFrequency(0);candidate.joint.setDampingRatio(.5);
    this.braces--;this.events.push({type:'brace',position:candidate.body.getPosition().clone()});return true;
  }
  step(input={},dt=STEP){
    if(this.status!=='playing')return;
    this.remember();this.t+=dt;
    if(input.build||input.steer||input.climb||input.brace)this.started=true;
    if(this.started)this.time+=dt;
    this.buildPulse=Math.max(0,this.buildPulse-dt*4.5);
    this.traverseClock=Math.max(0,this.traverseClock-dt);
    this.steering+=(clamp(input.steer||0,-1,1)-this.steering)*Math.min(1,dt*16);
    this.direction=clamp(input.steer||0,-1,1);
    this.buildVertical=input.build&&input.climb<0?-1:1;
    if(this.traversal){this.traversal.age+=dt;this.playerJoint.setMotorSpeed(this.traversal.distance/this.traversal.duration);}
    else if(this.playerJoint)this.playerJoint.setMotorSpeed(((this.mountSide?0:this.steering*.3)-this.playerJoint.getJointTranslation())*7);
    // Smooth deterministic gusts; the same level never changes its weather to force a failure.
    const phase=this.level.seed*.019;
    const envelope=.25+.75*Math.pow(Math.sin(this.t*.36+phase),4);
    this.wind=this.level.wind*(Math.sin(this.t*.7+phase)*.6+Math.sin(this.t*.23+phase)*.4)*envelope;
    if(this.options.wind!==undefined)this.wind=this.options.wind;
    for(const section of this.sections){
      section.age+=dt;
      const p=section.body.getPosition();
      section.body.applyForceToCenter(Vec2(this.wind*(1+Math.max(p.y,0)*.055),0),true);
    }
    if(input.brace)this.brace();
    if(!this.traversal&&!input.build&&this.traverseClock<=0&&(input.climb||input.steer)){
      const moved=input.climb?this.traverse(input.climb):this.walk(input.steer);this.traverseClock=moved?.34:.09;
    }
    this.buildClock-=dt;
    if(!this.traversal&&input.build&&this.buildClock<=0){
      const built=this.addSection(this.direction,false,this.buildVertical);
      this.buildClock=built?(this.equipment.fast?.264:.3):.08;
    }
    this.world.step(dt,12,8);
    if(this.traversal){
      const move=this.traversal,target=this.mountPoint(move.target,move.side),remaining=distance(this.player.getPosition(),target);
      if(remaining<.075||this.playerJoint.getJointTranslation()>move.distance-.055){
        const start=move.from.getPosition().clone();this.attachPlayer(move.target,move.side);
        this.events.push({type:move.kind==='walk'?'walk-end':'climb-end',position:this.player.getPosition().clone(),dx:this.player.getPosition().x-start.x,dy:this.player.getPosition().y-start.y,direction:move.direction});
      }else if(move.age>.72){
        const source=move.from;this.attachPlayer(source,move.side);this.events.push({type:'climb-abort',position:this.player.getPosition().clone()});
      }
    }
    for(const c of this.connections){
      if(c.broken)continue;
      const torque=Math.abs(c.joint.getReactionTorque(1/dt));
      const relative=Math.abs(c.body.getAngle()-c.lower.getAngle()-c.reference);
      c.stress=Math.max(torque/c.capacity,relative/(c.braced?.7:.36));
      c.overload=clamp(c.overload+dt*(c.stress>1?(c.stress-1)*1.5:-.7),0,2);
      if(!this.options.unbreakable&&(c.overload>.45||relative>.72)){
        const position=c.joint.getAnchorA().clone();
        // Capture the actual failed seam before releasing it. These are only
        // presentation coordinates; no impulse, timer or failure roll is added.
        const other=c.joint.getAnchorB().clone(),offset=c.lower.getLocalPoint(c.body.getPosition());
        const angle=Math.atan2(c.body.getPosition().y-c.lower.getPosition().y,c.body.getPosition().x-c.lower.getPosition().x);
        c.fracture={at:this.t,lower:c.lower.getLocalPoint(position),upper:c.body.getLocalPoint(other),lowerAngle:angle-c.lower.getAngle(),upperAngle:angle-c.body.getAngle(),span:Math.abs(offset.x)>Math.abs(offset.y)?SECTION:WIDTH};
        const velocity=c.body.getLinearVelocityFromWorldPoint(other).clone();
        this.world.destroyJoint(c.joint);c.broken=true;c.joint=null;this.breaks++;
        this.events.push({type:'break',position,normal:{x:Math.cos(angle),y:Math.sin(angle)},velocity,severity:clamp(c.stress,1,2),direction:Math.sign(velocity.x)||-Math.sign(c.body.getAngle())||1});
      }
    }
    if(this.traversal){
      const move=this.traversal,live=this.connections.some(c=>!c.broken&&((c.lower===move.from&&c.body===move.target)||(c.body===move.from&&c.lower===move.target)));
      if(!live){
        this.world.destroyJoint(this.playerJoint);this.playerJoint=null;this.traversal=null;this.support=null;this.mountSide=0;
        this.playerFixture.setFilterData({categoryBits:2,maskBits:1,groupIndex:0});
        this.events.push({type:'climb-break',position:this.player.getPosition().clone()});
      }
    }
    const p=this.player.getPosition();this.maxHeight=Math.max(this.maxHeight,this.height);
    if(!this.fragment&&distance(p,this.level.flag)<1.05){this.fragment=true;this.events.push({type:'fragment',position:Vec2(this.level.flag.x,this.level.flag.y)});}
    // Reach the exposed edge from above. A short clear step finishes the climb;
    // neither height under the rock nor a step through terrain can award a win.
    const destination={x:clamp(p.x,this.exitMin+.24,this.exitMax-.24),y:this.level.height+.3};
    const aboveLanding=this.height>=this.level.height-.025&&this.height<=this.level.height+2&&p.x>this.exitMin-.85&&p.x<this.exitMax+.85&&this.clearLine(p,destination);
    if(aboveLanding){this.status='won';this.events.push({type:'win',position:p.clone()});return;}
    const controlled=(this.traversal||this.mountSide)&&!!this.playerJoint&&this.grounded(this.top);
    const dropped=!controlled&&this.maxHeight-this.height>9;
    this.fallTime=dropped?this.fallTime+dt:0;
    if((p.y<-3.5||Math.abs(p.x)>24||this.fallTime>1.1)){
      this.status='lost';this.events.push({type:'lose',position:p.clone()});
    }
  }
  snapshot(){const p=this.player.getPosition();return{status:this.status,time:this.time,height:this.height,maxHeight:this.maxHeight,x:p.x,angle:this.top.getAngle(),angularVelocity:this.top.getAngularVelocity(),sections:this.sections.length,supportIndex:this.sections.findIndex(section=>section.body===this.top),breaks:this.breaks,wind:this.wind,fragment:this.fragment,braces:this.braces,blocked:this.blocked,maxStress:Math.max(...this.connections.filter(c=>!c.broken).map(c=>c.stress),0)};}
}
